/**
 * Three-pane Markdown editor for blog posts. Left = frontmatter form, center
 * = raw markdown textarea, right = live preview iframe.
 *
 * Autosave fires 800ms after the last keystroke (whichever pane). Publish
 * runs validation first; user can override only if no hard errors.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BRAND } from '@fin/shared/brand';
import { POST_STATUSES, type PostStatus } from '@fin/shared/enums';

import {
  type BlogPostWithBody,
  type BlogValidationResult,
  useAiGenerateBody,
  useAiGenerateFull,
  useAiGenerateHero,
  useAiGenerateMeta,
  useAiSuggestTitles,
  useContentPost,
  useContentVersions,
  usePreviewContentPost,
  usePublishContentPost,
  useRebuildContentPost,
  useRestoreContentVersion,
  useUnpublishContentPost,
  useUpdateContentPost,
  useUploadPostMedia,
  useValidateContentPost,
} from '../lib/queries';

const AUTOSAVE_DELAY_MS = 800;
const PREVIEW_DELAY_MS = 600;

interface DraftState {
  slug: string;
  title: string;
  description: string;
  category: string;
  tags: string;
  focusKeyword: string;
  author: string;
  heroSrc: string;
  heroAlt: string;
  body: string;
}

function postToDraft(p: BlogPostWithBody): DraftState {
  return {
    slug: p.slug,
    title: p.title,
    description: p.description,
    category: p.category ?? '',
    tags: (p.tags ?? []).join(', '),
    focusKeyword: p.focusKeyword ?? '',
    author: p.author ?? '',
    heroSrc: p.hero?.src ?? '',
    heroAlt: p.hero?.alt ?? '',
    body: p.body ?? '',
  };
}

function draftToPatch(d: DraftState) {
  const tagsArr = d.tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    slug: d.slug.trim(),
    title: d.title,
    description: d.description,
    category: d.category.trim() || undefined,
    tags: tagsArr.length ? tagsArr : undefined,
    focusKeyword: d.focusKeyword.trim() || undefined,
    author: d.author.trim() || undefined,
    hero: d.heroSrc.trim()
      ? { src: d.heroSrc.trim(), alt: d.heroAlt.trim() || d.title }
      : null,
    body: d.body,
  };
}

function draftToPreview(d: DraftState) {
  const tagsArr = d.tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    title: d.title,
    description: d.description,
    category: d.category.trim() || undefined,
    tags: tagsArr.length ? tagsArr : undefined,
    focusKeyword: d.focusKeyword.trim() || undefined,
    hero: d.heroSrc.trim()
      ? { src: d.heroSrc.trim(), alt: d.heroAlt.trim() || d.title }
      : null,
    body: d.body,
  };
}

export default function ContentEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const postQ = useContentPost(id);
  const updateMut = useUpdateContentPost();
  const previewMut = usePreviewContentPost();
  const validateMut = useValidateContentPost();
  const publishMut = usePublishContentPost();
  const unpublishMut = useUnpublishContentPost();
  const rebuildMut = useRebuildContentPost();

  // AI authoring helpers (super_admin + marketing on the server).
  const aiTitlesMut = useAiSuggestTitles();
  const aiMetaMut = useAiGenerateMeta();
  const aiBodyMut = useAiGenerateBody();
  const aiHeroMut = useAiGenerateHero();
  const aiFullMut = useAiGenerateFull();
  const uploadMut = useUploadPostMedia();
  const heroFileRef = useRef<HTMLInputElement>(null);
  const [aiBusy, setAiBusy] = useState<string | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [previewFull, setPreviewFull] = useState(false);
  useEffect(() => {
    if (!previewFull) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewFull]);
  const [validation, setValidation] = useState<BlogValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastSavedRef = useRef<string>('');
  const saveTimerRef = useRef<number | null>(null);
  const previewTimerRef = useRef<number | null>(null);

  // Reset draft when navigating between posts so stale data from a prior
  // post doesn't bleed into the new editor.
  useEffect(() => {
    setDraft(null);
    setSavedAt(null);
    setPreviewHtml('');
    setPreviewWarnings([]);
    setValidation(null);
    setError(null);
    setAiNotice(null);
    lastSavedRef.current = '';
  }, [id]);

  // Seed draft from the loaded post (keyed by id so re-fetches for a
  // different post correctly re-seed).
  useEffect(() => {
    if (postQ.data && postQ.data.postId === id && !draft) {
      const d = postToDraft(postQ.data);
      setDraft(d);
      lastSavedRef.current = JSON.stringify(d);
    }
  }, [postQ.data, draft, id]);

  const status = postQ.data?.status as PostStatus | undefined;

  // Autosave + live preview.
  useEffect(() => {
    if (!draft || !id) return;
    const serialized = JSON.stringify(draft);

    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      if (serialized === lastSavedRef.current) return;
      updateMut
        .mutateAsync({ id, patch: draftToPatch(draft) })
        .then(() => {
          lastSavedRef.current = serialized;
          setSavedAt(new Date().toISOString());
          setError(null);
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'Save failed.'));
    }, AUTOSAVE_DELAY_MS);

    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
    previewTimerRef.current = window.setTimeout(() => {
      previewMut
        .mutateAsync({ id, patch: draftToPreview(draft) })
        .then((r) => {
          setPreviewHtml(r.html);
          setPreviewWarnings(r.warnings);
        })
        .catch(() => undefined);
    }, PREVIEW_DELAY_MS);

    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current);
    };
    // updateMut/previewMut are stable references from React Query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, id]);

  const setField = useCallback(<K extends keyof DraftState>(k: K, v: DraftState[K]) => {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }, []);

  async function onValidate() {
    if (!id || !draft) return;
    try {
      const r = await validateMut.mutateAsync({ id, patch: draftToPreview(draft) });
      setValidation(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed.');
    }
  }

  async function onPublish() {
    if (!id || !draft) return;
    setError(null);
    try {
      // Validate first so the user sees errors before the request hits S3 / Build Lambda.
      const v = await validateMut.mutateAsync({ id, patch: draftToPreview(draft) });
      setValidation(v);
      if (!v.ok) {
        setError('Cannot publish: fix the errors below first.');
        return;
      }
      await publishMut.mutateAsync({ id });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publish failed.');
    }
  }

  async function onUnpublish() {
    if (!id) return;
    if (!confirm('Unpublish this post? It will be removed from the live blog on next build.')) return;
    try {
      await unpublishMut.mutateAsync(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unpublish failed.');
    }
  }

  // ── AI authoring actions ──────────────────────────────────────────────
  function aiErr(e: unknown): string {
    const msg = e instanceof Error ? e.message : 'AI request failed.';
    return /429|rate/i.test(msg) ? 'Daily AI quota reached (50 calls/day).' : msg;
  }

  async function onAiSuggestTitles() {
    if (!draft) return;
    const topic = prompt('Topic for title ideas?', draft.title || draft.focusKeyword || '');
    if (!topic) return;
    setAiBusy('titles'); setAiNotice(null); setError(null);
    try {
      const r = await aiTitlesMut.mutateAsync({ topic, focusKeyword: draft.focusKeyword || undefined, count: 5 });
      const picked = window.prompt(
        'Choose a title (copy/paste or pick a number 1-' + r.titles.length + '):\n\n' +
          r.titles.map((t, i) => `${i + 1}. ${t}`).join('\n'),
        r.titles[0] ?? '',
      );
      if (!picked) return;
      const idx = parseInt(picked.trim(), 10);
      const title = !isNaN(idx) && idx >= 1 && idx <= r.titles.length ? (r.titles[idx - 1] ?? picked) : picked;
      setField('title', title);
      setAiNotice('Title updated.');
    } catch (e) { setError(aiErr(e)); } finally { setAiBusy(null); }
  }

  async function onAiGenerateMeta() {
    if (!draft) return;
    if (!draft.title.trim()) { setError('Set a title first.'); return; }
    setAiBusy('meta'); setAiNotice(null); setError(null);
    try {
      const r = await aiMetaMut.mutateAsync({
        title: draft.title,
        body: draft.body || undefined,
        focusKeyword: draft.focusKeyword || undefined,
      });
      setField('description', r.description);
      setAiNotice('Meta description updated.');
    } catch (e) { setError(aiErr(e)); } finally { setAiBusy(null); }
  }

  async function onAiGenerateBody() {
    if (!draft) return;
    if (!draft.title.trim()) { setError('Set a title first.'); return; }
    if (draft.body.trim() && !confirm('Overwrite existing body with AI draft?')) return;
    setAiBusy('body'); setAiNotice(null); setError(null);
    try {
      const secondary = draft.tags
        .split(',').map((s) => s.trim()).filter(Boolean);
      const r = await aiBodyMut.mutateAsync({
        title: draft.title,
        focusKeyword: draft.focusKeyword || undefined,
        secondaryKeywords: secondary.length ? secondary : undefined,
        words: 900,
      });
      setField('body', r.markdown);
      setAiNotice(`Body drafted (${r.wordCount} words).`);
    } catch (e) { setError(aiErr(e)); } finally { setAiBusy(null); }
  }

  // Editor's own banner: JPG/PNG/WebP up to 5 MB, stored as hero-<ts>.<ext>.
  async function onUploadHero(file: File | undefined) {
    if (!file || !id) return;
    const ext = ({ 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' } as Record<string, string>)[file.type];
    setAiNotice(null); setError(null);
    if (!ext) { setError('Banner must be a JPG, PNG or WebP image.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Banner must be 5 MB or smaller.'); return; }
    try {
      const src = await uploadMut.mutateAsync({ id, file, filename: `hero-${Date.now()}.${ext}` });
      setField('heroSrc', src);
      setAiNotice(`Banner uploaded (${Math.round(file.size / 1024)} KB). Save to keep it.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (heroFileRef.current) heroFileRef.current.value = '';
    }
  }

  async function onAiGenerateHero() {
    if (!draft) return;
    const slug = draft.slug.trim();
    if (!slug) { setError('Save a slug first.'); return; }
    const promptText =
      `${draft.title || draft.focusKeyword || 'Handyman services in Dubai'}, ` +
      'photorealistic, bright natural light, professional, clean composition, no text';
    setAiBusy('hero'); setAiNotice(null); setError(null);
    try {
      const r = await aiHeroMut.mutateAsync({ slug, prompt: promptText, alt: draft.title || undefined });
      setField('heroSrc', r.src);
      if (!draft.heroAlt.trim()) setField('heroAlt', r.alt);
      setAiNotice(`Hero image generated (${Math.round(r.bytes / 1024)} KB).`);
    } catch (e) { setError(aiErr(e)); } finally { setAiBusy(null); }
  }

  async function onAiGenerateFull() {
    if (!draft) return;
    const topic = prompt(
      'Topic for full post (will overwrite title, description, body)?',
      draft.title || draft.focusKeyword || '',
    );
    if (!topic) return;
    if ((draft.title || draft.body) && !confirm('Overwrite existing title/description/body?')) return;
    setAiBusy('full'); setAiNotice(null); setError(null);
    try {
      const r = await aiFullMut.mutateAsync({
        topic,
        focusKeyword: draft.focusKeyword || undefined,
        words: 900,
      });
      const finalSlug = draft.slug.trim() || r.suggestedSlug;
      setDraft((d) => d ? {
        ...d,
        title: r.title,
        description: r.description,
        body: r.markdown,
        slug: finalSlug,
      } : d);
      setAiNotice(`Full post drafted (${r.wordCount} words). Generating hero image…`);

      // Chain hero image generation so a single click produces a fully-ready
      // post. If the hero call fails (quota, model error), we keep the text
      // and surface the error — the post is still usable without an image.
      try {
        setAiBusy('hero');
        const heroPrompt =
          `${r.title}, photorealistic, bright natural light, professional, clean composition, no text`;
        const h = await aiHeroMut.mutateAsync({ slug: finalSlug, prompt: heroPrompt, alt: r.title });
        setDraft((d) => d ? { ...d, heroSrc: h.src, heroAlt: d.heroAlt.trim() || h.alt } : d);
        setAiNotice(`Full post drafted (${r.wordCount} words) + hero image (${Math.round(h.bytes / 1024)} KB).`);
      } catch (heroErr) {
        setAiNotice(`Full post drafted (${r.wordCount} words). Hero image failed — click "Generate hero image" to retry.`);
        // Don't setError — the text result succeeded; show non-fatal note.
        console.warn('Hero auto-generation failed:', heroErr);
      }
    } catch (e) { setError(aiErr(e)); } finally { setAiBusy(null); }
  }

  if (postQ.isLoading || !draft) {
    return <div className="p-6 text-stone-500">Loading…</div>;
  }
  if (postQ.error) {
    return (
      <div className="p-6 text-red-600">
        Failed to load post: {postQ.error instanceof Error ? postQ.error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            type="button"
            className="text-sm text-stone-500 hover:underline"
            onClick={() => navigate('/content')}
          >
            ← Back to content
          </button>
          <h1 className="mt-1 text-xl font-semibold" style={{ color: BRAND.text }}>
            {draft.title || 'Untitled draft'}
          </h1>
          <p className="text-xs text-stone-500">
            Status: <span className="font-medium capitalize">{status}</span>
            {savedAt && (
              <span className="ml-3">Saved {new Date(savedAt).toLocaleTimeString()}</span>
            )}
            {updateMut.isPending && <span className="ml-3 text-amber-600">Saving…</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50"
            onClick={onValidate}
            disabled={validateMut.isPending}
          >
            {validateMut.isPending ? 'Validating…' : 'Validate'}
          </button>
          {status !== 'published' ? (
            <button
              type="button"
              className="rounded px-3 py-1.5 text-sm font-medium text-white"
              style={{ backgroundColor: BRAND.primary }}
              onClick={onPublish}
              disabled={publishMut.isPending}
            >
              {publishMut.isPending ? 'Publishing…' : 'Publish'}
            </button>
          ) : (
            <>
              <a
                href={`https://fixitninjaz.com/blog/${draft.slug.trim()}/`}
                target="_blank"
                rel="noreferrer"
                className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50"
                title="Open the published post in a new tab."
              >
                View on site ↗
              </a>
              <button
                type="button"
                className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50 disabled:opacity-60"
                onClick={() => {
                  if (!id) return;
                  void rebuildMut.mutateAsync(id);
                }}
                disabled={rebuildMut.isPending}
                title="Re-render this post and invalidate the CDN cache."
              >
                {rebuildMut.isPending ? 'Rebuilding…' : 'Rebuild'}
              </button>
              <button
                type="button"
                className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50"
                onClick={onUnpublish}
                disabled={unpublishMut.isPending}
              >
                {unpublishMut.isPending ? 'Unpublishing…' : 'Unpublish'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {validation && (
        <ValidationPanel validation={validation} />
      )}

      {/* AI authoring toolbar (super_admin + marketing on server) */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
            AI Assist · Bedrock
          </div>
          <div className="text-xs text-stone-500">Daily quota: 50 calls per author</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <AiButton label="Generate full post" busy={aiBusy === 'full'} onClick={onAiGenerateFull} primary />
          <AiButton label="Suggest titles" busy={aiBusy === 'titles'} onClick={onAiSuggestTitles} />
          <AiButton label="Generate meta" busy={aiBusy === 'meta'} onClick={onAiGenerateMeta} />
          <AiButton label="Draft body" busy={aiBusy === 'body'} onClick={onAiGenerateBody} />
          <AiButton label="Generate hero image" busy={aiBusy === 'hero'} onClick={onAiGenerateHero} />
        </div>
        {aiNotice && <div className="mt-2 text-xs text-indigo-700">{aiNotice}</div>}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[320px_minmax(0,1fr)_minmax(0,1fr)]">
        {/* Frontmatter ─────────────────────────────────────────── */}
        <aside className="space-y-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-stone-700">Frontmatter</h2>
          <Field label="Slug">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm font-mono"
              value={draft.slug}
              onChange={(e) => setField('slug', e.target.value)}
              pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
              maxLength={80}
            />
          </Field>
          <Field label="Title">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.title}
              onChange={(e) => setField('title', e.target.value)}
              maxLength={200}
            />
            <CharCount value={draft.title} min={20} max={70} />
          </Field>
          <Field label="Meta description">
            <textarea
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.description}
              rows={3}
              onChange={(e) => setField('description', e.target.value)}
              maxLength={300}
            />
            <CharCount value={draft.description} min={80} max={160} />
          </Field>
          <Field label="Category">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.category}
              onChange={(e) => setField('category', e.target.value)}
              maxLength={80}
            />
          </Field>
          <Field label="Tags (comma-separated)">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.tags}
              onChange={(e) => setField('tags', e.target.value)}
            />
          </Field>
          <Field label="Focus keyword">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.focusKeyword}
              onChange={(e) => setField('focusKeyword', e.target.value)}
              maxLength={120}
            />
          </Field>
          <Field label="Author">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.author}
              onChange={(e) => setField('author', e.target.value)}
              maxLength={120}
            />
          </Field>
          <Field label="Hero image URL">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.heroSrc}
              onChange={(e) => setField('heroSrc', e.target.value)}
              placeholder="/media/<slug>/hero.jpg"
            />
            <input
              ref={heroFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => void onUploadHero(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => heroFileRef.current?.click()}
              disabled={uploadMut.isPending}
              className="mt-1 rounded border border-stone-300 bg-white px-2 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              title="Upload your own banner (JPG, PNG or WebP, max 5 MB). 1600×900 (16:9) works best."
            >
              {uploadMut.isPending ? 'Uploading…' : 'Upload banner'}
            </button>
          </Field>
          <Field label="Hero alt text">
            <input
              className="w-full rounded border border-stone-300 px-2 py-1 text-sm"
              value={draft.heroAlt}
              onChange={(e) => setField('heroAlt', e.target.value)}
              maxLength={300}
            />
          </Field>
          <VersionsBlock postId={id} />
        </aside>

        {/* Editor ──────────────────────────────────────────────── */}
        <section className="flex flex-col rounded-lg border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-stone-200 px-3 py-2">
            <span className="text-sm font-semibold text-stone-700">Markdown</span>
            <span className="text-xs text-stone-500">{countWords(draft.body)} words</span>
          </div>
          <textarea
            className="min-h-[600px] flex-1 resize-none rounded-b-lg p-3 font-mono text-sm focus:outline-none"
            value={draft.body}
            onChange={(e) => setField('body', e.target.value)}
            spellCheck={false}
            placeholder="# Heading&#10;&#10;Write your post here…"
          />
        </section>

        {/* Preview ─────────────────────────────────────────────── */}
        <section
          className={
            previewFull
              ? 'fixed inset-0 z-50 flex flex-col bg-white'
              : 'flex flex-col rounded-lg border border-stone-200 bg-white shadow-sm'
          }
        >
          <div className="flex items-center justify-between border-b border-stone-200 px-3 py-2">
            <span className="text-sm font-semibold text-stone-700">Preview</span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-stone-500">{previewMut.isPending ? 'Rendering…' : 'Live'}</span>
              <button
                type="button"
                onClick={() => setPreviewFull((v) => !v)}
                className="rounded border border-stone-300 px-2 py-0.5 text-xs font-medium text-stone-700 hover:bg-stone-100"
                title={previewFull ? 'Exit full screen (Esc)' : 'Full screen preview'}
              >
                {previewFull ? '✕ Exit full screen' : '⛶ Full screen'}
              </button>
            </div>
          </div>
          <iframe
            title="Preview"
            srcDoc={previewHtml}
            className={
              previewFull
                ? 'flex-1 border-0'
                : 'min-h-[600px] flex-1 rounded-b-lg border-0'
            }
            sandbox="allow-same-origin"
          />
          {previewWarnings.length > 0 && (
            <ul className="border-t border-stone-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {previewWarnings.map((w, i) => (
                <li key={i}>⚠ {w}</li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium text-stone-600">{label}</span>
      {children}
    </label>
  );
}

function CharCount({ value, min, max }: { value: string; min: number; max: number }) {
  const len = value.length;
  const tone = len < min ? 'text-amber-600' : len > max ? 'text-red-600' : 'text-green-700';
  return <span className={`mt-0.5 block text-[11px] ${tone}`}>{len} chars (recommend {min}–{max})</span>;
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function VersionsBlock({ postId }: { postId: string | undefined }) {
  const versionsQ = useContentVersions(postId);
  const restoreMut = useRestoreContentVersion();
  const versions = useMemo(() => versionsQ.data ?? [], [versionsQ.data]);
  if (versionsQ.isLoading) return null;
  if (!versions.length) return null;

  return (
    <div className="mt-3 border-t border-stone-200 pt-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
        Recent versions
      </h3>
      <ul className="space-y-1 text-xs">
        {versions.slice(0, 5).map((v) => (
          <li key={v.versionId} className="flex items-center justify-between gap-2">
            <span className="truncate text-stone-600">
              {v.lastModified ? new Date(v.lastModified).toLocaleString() : v.versionId.slice(0, 10)}
              {v.isLatest && <span className="ml-1 rounded bg-green-100 px-1 text-[10px] text-green-700">current</span>}
            </span>
            {!v.isLatest && postId && (
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => {
                  if (!confirm('Restore this version? Your current draft body will be overwritten on next reload.')) return;
                  void restoreMut.mutateAsync({ id: postId, versionId: v.versionId });
                }}
              >
                Restore
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ValidationPanel({ validation }: { validation: BlogValidationResult }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-stone-700">
          SEO score: <span className={validation.score >= 80 ? 'text-green-700' : validation.score >= 60 ? 'text-amber-600' : 'text-red-600'}>{validation.score}/100</span>
        </span>
        <span className={`text-xs ${validation.ok ? 'text-green-700' : 'text-red-700'}`}>
          {validation.ok ? 'Ready to publish' : 'Has blocking errors'}
        </span>
      </div>
      {validation.errors.length > 0 && (
        <ul className="mb-2 space-y-0.5 text-xs">
          {validation.errors.map((e, i) => (
            <li key={i} className="text-red-700">
              ✗ <span className="font-medium">{e.code}:</span> {e.message}
            </li>
          ))}
        </ul>
      )}
      {validation.warnings.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {validation.warnings.map((w, i) => (
            <li key={i} className="text-amber-700">
              ⚠ <span className="font-medium">{w.code}:</span> {w.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Suppress lint warning for unused enum import — keeping in case future status switcher needs it.
void POST_STATUSES;

function AiButton({ label, busy, onClick, primary }: { label: string; busy: boolean; onClick: () => void; primary?: boolean }) {
  const base = primary
    ? 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50'
    : 'bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-100 disabled:opacity-50';
  return (
    <button type="button" onClick={onClick} disabled={busy} className={'rounded px-3 py-1.5 text-xs font-medium ' + base}>
      {busy ? 'Working�' : label}
    </button>
  );
}
